import React from "react";
import { Type } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "./ui/input";
import { loadGoogleFont } from "../lib/font-loader";
import { POPULAR_FONTS } from "../lib/constants";

interface FontPickerProps {
  value: string;
  onChange: (font: string) => void;
}

export function FontPicker({ value, onChange }: FontPickerProps) {
  const [search, setSearch] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filteredFonts = POPULAR_FONTS.filter(f => f.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = filteredFonts.some(f => f.toLowerCase() === search.toLowerCase());

  React.useEffect(() => {
    if (isOpen) {
      if (search && !exactMatch) {
        loadGoogleFont(search).catch(() => {});
      }
      filteredFonts.slice(0, 20).forEach(font => loadGoogleFont(font).catch(() => {}));
    }
  }, [isOpen, search, exactMatch, filteredFonts]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="input-with-icon cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
        <Type className="size-4" />
        <div className="studio-input flex items-center pl-9 pr-4 text-sm font-medium">
          {value || "Select a font..."}
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-50 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-secondary p-1 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="sticky top-0 z-10 bg-secondary pb-1">
            <Input
              autoFocus
              placeholder="Search fonts..."
              className="h-9 border-none bg-background/50 focus-visible:ring-0"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="grid gap-px">
            {search && !exactMatch && (
              <button
                className="flex items-center justify-between rounded-md px-3 py-3 text-sm transition-colors bg-primary/5 hover:bg-primary/10 text-primary border-b border-primary/10"
                onClick={() => {
                  onChange(search);
                  setIsOpen(false);
                  setSearch("");
                }}
              >
                <div className="flex flex-col items-start">
                  <span className="text-[10px] uppercase font-bold opacity-60">Use Custom Font</span>
                  <span style={{ fontFamily: `"${search}", sans-serif` }}>{search}</span>
                </div>
              </button>
            )}
            {filteredFonts.map((font) => (
              <button
                key={font}
                className={cn(
                  "flex items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-primary/10 hover:text-primary",
                  value === font ? "bg-primary/20 text-primary font-bold" : "text-foreground/80"
                )}
                onClick={() => {
                  onChange(font);
                  setIsOpen(false);
                  setSearch("");
                }}
              >
                <span style={{ fontFamily: `"${font}", sans-serif` }}>{font}</span>
              </button>
            ))}
            {filteredFonts.length === 0 && !search && (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                No fonts found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
